import { useEffect } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";

const UTM_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
];

/**
 * Captures UTM parameters from the URL query string, stores them
 * in sessionStorage, then cleans the URL so the params don't linger.
 *
 * Always overwrites with the latest UTM data so that the most recent
 * source is what gets passed to Calendly.
 *
 * Usage example QR code URLs:
 *   regodit.com/?utm_source=tv_ad&utm_medium=qr&utm_campaign=launch
 *   regodit.com/?utm_source=linkedin&utm_medium=qr&utm_campaign=launch
 */
export default function useUTMTracking() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const utmData = {};
    let hasUtm = false;

    UTM_PARAMS.forEach((param) => {
      const value = searchParams.get(param);
      if (value) {
        utmData[param] = value;
        hasUtm = true;
      }
    });

    // Only proceed if at least one UTM param is present
    if (!hasUtm) return;

    // Always overwrite with the latest source
    sessionStorage.setItem("utm_data", JSON.stringify(utmData));

    // Remove UTM params from the URL to keep it clean
    const cleanParams = new URLSearchParams(searchParams);
    UTM_PARAMS.forEach((param) => cleanParams.delete(param));

    const remaining = cleanParams.toString();
    const cleanPath = location.pathname + (remaining ? `?${remaining}` : "");

    navigate(cleanPath, { replace: true });
  }, [searchParams, navigate, location.pathname]);
}
